import { Component, Input } from '@builder.io/sdk';

import sectionFields from 'molecules/Section/config';

import { ResourceConversionPanelConfigProps } from 'organisms/Cards/ResourceConversionPanel/config';
import { ResourceConversionPanelReferenceProps } from 'organisms/Cards/ResourceConversionPanel/types';

const listingFields = [
  {
    name: 'type',
    type: 'string',
    enum: [
      { label: 'All Resources', value: 'resources' },
      { label: 'Blog', value: 'blog' },
      { label: 'Connectors', value: 'connectors' },
      { label: 'Customer Stories', value: 'customerStories' },
      { label: 'Events', value: 'events' },
      { label: 'Guides', value: 'guides' },
      { label: 'Press', value: 'press' },
      { label: 'Tools and Insights', value: 'tools' },
      { label: 'Videos', value: 'videos' },
    ],
    defaultValue: 'blog',
    helperText: 'Choose which content type to list. Controls filters, cards, and layout nuances.',
  },
  {
    name: 'sectionHeading',
    type: 'string',
    defaultValue: 'All Resources',
    helperText: 'Optional heading shown above the listing section.',
  },
  {
    name: 'filterLabels',
    type: 'list',
    subFields: [
      {
        name: 'label',
        type: 'string',
        helperText: 'Visible label for the filter (e.g., “All”, “Product”).',
      },
      {
        name: 'value',
        type: 'string',
        helperText: 'API/slug value used to filter content programmatically.',
      },
      {
        name: 'heading',
        type: 'string',
        helperText: 'Optional heading to show when this filter is active.',
      },
      {
        name: 'anchorId',
        type: 'string',
        helperText: 'Optional anchor id used for JumpNav. Example: "customer-stories" → href="#customer-stories".',
      },
    ],
    helperText: 'Configure filter options displayed above the list.',
  },
  {
    name: 'searchPlaceholder',
    type: 'string',
    defaultValue: 'Search posts',
    helperText: 'Placeholder text for the search input.',
    showIf: "options.get('type') !== 'resources'",
  },
  {
    name: 'headingTitle',
    type: 'string',
    defaultValue: 'All Posts',
    helperText: 'Page/section title used near the search and filters.',
    showIf: "options.get('type') !== 'resources'",
  },
  {
    name: 'conversionPanel',
    type: 'reference',
    model: 'resource-conversion-panel',
    showIf: "options.get('type') === 'connectors'",
    helperText: 'Primary conversion panel shown alongside the listing (connectors only).',
  },
  {
    name: 'conversionPanelSecondary',
    type: 'reference',
    model: 'resource-conversion-panel',
    showIf: "options.get('type') === 'connectors'",
    helperText: 'Optional secondary conversion panel (connectors only).',
  },
  ...sectionFields,
] as const satisfies DeepReadonly<Input[]>;

const listingConfig = {
  name: 'Listing',
  inputs: listingFields,
} as Component;

type ListingFilterLabel = {
  label?: string;
  value?: string;
  heading?: string;
  anchorId?: string;
};

export type ListingConfigProps = Omit<
  GenerateBuilder<typeof listingFields>,
  'conversionPanel' | 'conversionPanelSecondary' | 'newsletterCard'
> & {
  newsletterCard?: BuilderReference<ResourceConversionPanelConfigProps>;
  sectionHeading?: string;
  filterLabels?: ListingFilterLabel[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  builderState: any;
} & ResourceConversionPanelReferenceProps;

export default listingConfig;
